import { useEffect, useId, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api,
  Cliente,
  ControlDeEnvio,
  type GuiaDePresentacion,
  ErrorDeApi,
  EstadoDeEnvio,
  type PlazosDelPeriodo,
  ResumenDeCliente,
  TipoArchivoContabilidadElectronica,
  TipoEnvioBalanza,
  TipoPersona,
  TipoSolicitudFiscalizacion,
} from '../servicios/api'
import { plazoRelativo } from './Tablero'
import { fechaContable } from '../fechas'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import { telon } from '../ganchos/telon'
import { useAvisos } from '../componentes/Toast'
import { AyudaDeCampo } from '../ayuda/AyudaDeCampo'

const NOMBRE_DE_TIPO_ARCHIVO: Record<ControlDeEnvio['tipoArchivo'], string> = {
  Ct: 'CT · Catálogo de cuentas',
  Bn: 'BN · Balanza normal',
  Bc: 'BC · Balanza complementaria',
  Pl: 'PL · Pólizas del periodo',
  Xf: 'XF · Auxiliar de folios',
  Xc: 'XC · Auxiliar de cuentas',
}

const INSIGNIA_DE_ESTADO: Record<EstadoDeEnvio, string> = {
  Generado: 'aviso',
  Enviado: 'aviso',
  Aceptado: 'vigente',
  Rechazado: 'vencido',
}

const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-MX') : '—')

/** El periodo tal como lo lee el contador: 2026-09. */
const periodo = (control: Pick<ControlDeEnvio, 'anio' | 'mes'>) =>
  `${control.anio}-${String(control.mes).padStart(2, '0')}`

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

/** Los plazos del Anexo 24 difieren entre persona moral y física: el chip lo dice de entrada. */
const ABREVIATURA_DE_PERSONA: Record<TipoPersona, string> = { Moral: 'PM', Fisica: 'PF' }

/**
 * Días naturales de hoy a la fecha límite; negativo si ya venció. Es copia de la de Tablero.tsx
 * —allá no está exportada— y solo decide el color del chip. El texto, que es lo que el contador
 * lee, sí viene de `plazoRelativo`: las dos pantallas tienen que decir lo mismo del mismo plazo.
 */
const diasHasta = (iso: string, hoy: Date) => {
  const limite = new Date(`${iso}T00:00:00`)
  const inicioDeHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
  return Math.round((limite.getTime() - inicioDeHoy.getTime()) / 86_400_000)
}

const fechaLarga = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('es-MX', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

/** Un plazo legal de balanza listo para pintarse en la franja (RF-806, RN-08). */
export interface ChipDePlazo {
  clave: string
  /** «PM jun 2026», «PF · balanza ajustada 2025». */
  etiqueta: string
  /** Fecha límite en AAAA-MM-DD, tal como la calcula el servidor con los días hábiles. */
  fechaLimite: string
  /** El envío ya se hizo: el plazo queda de referencia, no de cuenta regresiva. */
  cumplida?: boolean
  /** A2 · El día N del mes a secas, antes de descontar inhábiles. Solo si el servidor lo dio. */
  fechaSinCalendario?: string
  /** A2 · El calendario oficial corrió el vencimiento; la franja lo explica en vez de esconderlo. */
  diaInhabilRecorrido?: boolean
}

/**
 * A2 · La franja de plazos del prototipo (RF-806): un chip por plazo, el vencido relleno y el
 * que urge acentuado, para que la prisa se vea sin leer fechas.
 *
 * Ninguna fecha se calcula aquí. El calendario de días inhábiles (Art. 74 LFT) y la
 * parametrización de RN-08 viven en el servidor; el navegador solo recibe la fecha límite y la
 * traduce a «mañana» o «venció hace 3 días» con el mismo `plazoRelativo` del tablero.
 */
export function FranjaDePlazos({ plazos, hoy }: { plazos: ChipDePlazo[]; hoy: Date }) {
  // A2 · Los que el calendario oficial corrió. Un vencimiento que no cae donde la regla dice
  // («los primeros 3 días») obliga al contador a rehacer la cuenta para confiar en él; decir de
  // dónde salió es la misma exigencia que no presentar un número sin su procedencia.
  const recorridos = plazos.filter((p) => p.diaInhabilRecorrido && p.fechaSinCalendario)

  return (
    <section className="plazos" style={{ padding: 'var(--space-3) 0 0' }}
      aria-label="Calendario de plazos de la balanza electrónica">
      <span className="kicker">Calendario · RF-806</span>
      {plazos.map((plazo) => {
        const dias = diasHasta(plazo.fechaLimite, hoy)
        const urgencia = plazo.cumplida ? '' : dias < 0 ? ' vencio' : dias <= 1 ? ' urge' : ''
        return (
          <span key={plazo.clave} className={`plazo-chip${urgencia}`} title={fechaLarga(plazo.fechaLimite)}>
            {plazo.etiqueta} — {plazo.cumplida ? 'ya enviada' : plazoRelativo(plazo.fechaLimite, hoy)}
          </span>
        )
      })}
      <span className="glosa">Plazos parametrizables (RN-08) · considera días inhábiles oficiales</span>
      {recorridos.map((plazo) => (
        <span key={`${plazo.clave}-recorrido`} className="glosa" style={{ flexBasis: '100%' }}>
          {plazo.etiqueta}: contar solo días hábiles corrió el vencimiento del{' '}
          {fechaContable(plazo.fechaSinCalendario!)} al {fechaContable(plazo.fechaLimite)}.
        </span>
      ))}
    </section>
  )
}

/**
 * Los dos plazos de balanza que le corren a un cliente: el mensual del Anexo 24 y el del
 * cierre del ejercicio (periodo 13). Los dos los calcula el servidor; aquí solo se rotulan.
 */
export function plazosDelCliente(resumen: ResumenDeCliente): ChipDePlazo[] {
  const persona = ABREVIATURA_DE_PERSONA[resumen.tipoPersona]
  const mensual = resumen.proximaObligacion
  return [
    {
      clave: 'mensual',
      etiqueta: `${persona} ${MESES_CORTOS[mensual.mes - 1]} ${mensual.anio}`,
      fechaLimite: mensual.fechaLimite,
      cumplida: mensual.yaCumplida,
    },
    {
      clave: 'ajustada',
      etiqueta: `${persona} · balanza ajustada ${resumen.anioDelEjercicioBalanzaAjustada}`,
      fechaLimite: resumen.limiteBalanzaAjustada,
      cumplida: resumen.ajusteDeCierreCumplido,
    },
  ]
}

/**
 * A2 · Los plazos del periodo vistos desde el despacho: uno por tipo de persona, porque para el
 * MISMO mes una persona moral vence dos días hábiles antes que una física.
 *
 * Deliberadamente ninguno se marca `cumplida`. El listado del despacho no trae el tipo de
 * persona de cada cliente, así que no hay forma de saber si «las morales ya cumplieron»; y un
 * cliente que nunca generó archivo no aparece en ese listado, de modo que «no hay pendientes»
 * tampoco significaría «todos cumplieron». Quién cumplió lo dice la tabla, renglón por renglón.
 */
export function plazosDelPeriodo(respuesta: PlazosDelPeriodo): ChipDePlazo[] {
  return respuesta.plazos.map((plazo) => ({
    clave: plazo.tipoPersona,
    etiqueta: `${ABREVIATURA_DE_PERSONA[plazo.tipoPersona]} ${MESES_CORTOS[respuesta.mes - 1]} ${respuesta.anio}`,
    fechaLimite: plazo.fechaLimite,
    fechaSinCalendario: plazo.fechaSinCalendario,
    diaInhabilRecorrido: plazo.diaInhabilRecorrido,
  }))
}

/**
 * A3 · Un rechazo del SAT deja de ser un texto dentro de una celda: banner con la causa, el
 * archivo al que se le atribuye y la acción que lo desbloquea.
 *
 * No lleva la línea «XSD error: cvc-complex-type.4 …» del prototipo, y no es un olvido: ese
 * detalle no existe en ninguna parte. La validación contra el XSD revienta ANTES de producir el
 * ZIP, así que un control nunca nace con un fallo de esquema y el texto del error no se guarda.
 * Lo real es el motivo que teclea el contador al registrar el acuse, más el nombre del archivo
 * y los esquemas contra los que sí se validó: eso es lo que se pinta.
 */
export function BannerDeRechazo({
  sujeto,
  control,
  clienteId,
}: {
  /** A quién y a qué se le atribuye el rechazo: «BN · Balanza normal» o «Autopartes · BN». */
  sujeto: string
  control: Pick<ControlDeEnvio, 'anio' | 'mes' | 'motivoRechazo' | 'nombreDeArchivo' | 'esquemasXsd'>
  clienteId: string
}) {
  return (
    <div className="aviso-critico" role="alert">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
      <div>
        <strong>Rechazo del SAT · {sujeto} · periodo {periodo(control)}.</strong>{' '}
        {control.motivoRechazo ?? 'El SAT lo rechazó y no se registró el motivo.'}{' '}
        Corrige lo que señala el acuse y vuelve a generar el archivo; si el periodo ya estaba
        enviado, procede balanza complementaria (BC).
        <div className="mono detalle-tecnico">
          {control.nombreDeArchivo ?? 'sin nombre de archivo registrado'}
          {control.esquemasXsd ? ` · validado contra ${control.esquemasXsd}` : ''}
        </div>
      </div>
      <Link className="boton primario" to={`/clientes/${clienteId}/catalogo`}>Corregir catálogo</Link>
    </div>
  )
}

/**
 * A4 · Los cuatro segmentos del prototipo. No son los seis tipos sueltos a propósito: el
 * contador busca «la balanza», «el catálogo» o «lo que pidió el SAT», y PL, XF y XC siempre
 * viajan juntos en una misma solicitud de fiscalización (RF-803).
 */
export const GRUPOS_DE_ARCHIVO: { clave: string; etiqueta: string; tipos: TipoArchivoContabilidadElectronica[] }[] = [
  { clave: 'Bn', etiqueta: 'BN · balanza', tipos: ['Bn'] },
  { clave: 'Ct', etiqueta: 'CT · catálogo', tipos: ['Ct'] },
  { clave: 'Bc', etiqueta: 'BC', tipos: ['Bc'] },
  { clave: 'Solicitud', etiqueta: 'PL / XF / XC', tipos: ['Pl', 'Xf', 'Xc'] },
]

/**
 * Resultado de la validación XSD de un archivo (A7). No hay variante «✗»: la validación
 * revienta antes de producir el ZIP, así que un control solo existe si el archivo era válido.
 * El «—» es para los controles anteriores a que se guardara el dato, no para un fallo.
 */
export function SelloXsd({ control }: { control: Pick<ControlDeEnvio, 'esquemasXsd' | 'validadoEnUtc'> }) {
  if (!control.esquemasXsd) {
    return <span className="sutil" title="Generado antes de que se registrara el esquema.">—</span>
  }
  const cuando = control.validadoEnUtc ? ` el ${new Date(control.validadoEnUtc).toLocaleDateString('es-MX')}` : ''
  return (
    <span className="insignia vigente" title={`Validado${cuando} contra ${control.esquemasXsd}`}>
      ✓ válido
    </span>
  )
}

/**
 * Seguimiento de los archivos del Anexo 24 por cliente y periodo: generado → enviado →
 * aceptado/rechazado, con el resguardo del acuse del SAT. El envío por el Buzón Tributario
 * es manual del contador; aquí solo se registra. SRS: RF-805. Cédula: G-08.
 */
/**
 * RF-1603 · La guía de presentación del archivo: qué se sube, en qué apartado del portal, con
 * qué firma y hasta cuándo. El texto viene del servidor porque es configurable (RN-64): el SAT
 * reacomoda su portal y corregirlo no debe costar un despliegue.
 */
export function ModalDeGuia({
  datos, alCerrar,
}: {
  datos: { control: ControlDeEnvio; guia: GuiaDePresentacion }
  alCerrar: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, alCerrar)
  const { control, guia } = datos

  return (
    <div className="modal" {...telon(alCerrar)}>
      <div className="panel" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={idTitulo}>
        <h2 id={idTitulo}>
          Cómo se presenta · {NOMBRE_DE_TIPO_ARCHIVO[control.tipoArchivo]} {periodo(control)}
        </h2>

        <dl className="datos">
          <dt>Qué es</dt>
          <dd>{guia.que}</dd>
          <dt>Qué se sube</dt>
          <dd className="mono">{guia.nombreDeArchivo ?? 'El ZIP tal como se descarga de esta pantalla.'}</dd>
          <dt>Dónde</dt>
          <dd>{guia.donde}</dd>
          <dt>Con qué</dt>
          <dd>{guia.conQue}</dd>
          <dt>Hasta cuándo</dt>
          <dd>
            {guia.fechaLimite
              ? new Date(`${guia.fechaLimite}T00:00:00`).toLocaleDateString('es-MX', {
                  day: '2-digit', month: 'long', year: 'numeric',
                })
              : 'Sin plazo mensual: se presenta cuando corresponde, no cada mes.'}
          </dd>
        </dl>

        <p className="nota">{guia.nota}</p>

        {guia.vigenteDesde && (
          <p className="menuda">
            Instrucciones vigentes desde{' '}
            {new Date(`${guia.vigenteDesde}T00:00:00`).toLocaleDateString('es-MX')}. Si el SAT
            cambió su portal después, avísale a quien administra la plataforma.
          </p>
        )}

        <div className="acciones">
          <button className="primario" onClick={alCerrar}>Entendido</button>
        </div>
      </div>
    </div>
  )
}

export function ControlDeEnvios() {
  const { id: clienteId } = useParams<{ id: string }>()
  const [cliente, setCliente] = useState<Cliente>()
  const [controles, setControles] = useState<ControlDeEnvio[]>()
  const [error, setError] = useState('')
  const [descargando, setDescargando] = useState<string | null>(null)
  const [guia, setGuia] = useState<{ control: ControlDeEnvio; guia: GuiaDePresentacion } | null>(null)
  const avisar = useAvisos()

  /**
   * RF-1600 · Recupera el archivo que el contador tiene que subir al Buzón. El servidor lo
   * vuelve a generar y avisa si el contenido ya no coincide con el que se presentó (RN-60):
   * eso pasa cuando el catálogo o las pólizas del periodo cambiaron después del envío, y
   * entregarlo callando sería darle otro documento sin que se entere.
   */
  /** RF-1603 · Qué se sube, dónde y hasta cuándo, sin salir de la pantalla. */
  async function verGuia(control: ControlDeEnvio) {
    setError('')
    try {
      setGuia({ control, guia: await api.guiaDePresentacion(clienteId!, control.id) })
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar la guía de presentación.')
    }
  }

  /** RF-1604 · Recupera el acuse que el SAT devolvió, resguardado como archivo. */
  async function descargarAcuse(control: ControlDeEnvio) {
    setError('')
    try {
      await api.descargarAcuseArchivo(clienteId!, control.id)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo descargar el acuse resguardado.')
    }
  }

  async function descargar(control: ControlDeEnvio) {
    setDescargando(control.id)
    setError('')
    try {
      const { divergente, resguardado } = await api.descargarArchivoDeEnvio(clienteId!, control.id)
      const nombre = NOMBRE_DE_TIPO_ARCHIVO[control.tipoArchivo]
      avisar(
        resguardado
          ? `${nombre} descargado: es el archivo tal como se generó, byte a byte. Súbelo al Buzón Tributario y registra el acuse.`
          : divergente
            ? `${nombre} descargado, pero OJO: no había copia resguardada y se regeneró, así que su contenido ya no es el que se presentó al SAT.`
            : `${nombre} descargado. No había copia resguardada, así que se regeneró, pero coincide con lo presentado. Súbelo al Buzón Tributario y registra el acuse.`,
      )
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo generar el archivo para descargar.')
    } finally {
      setDescargando(null)
    }
  }
  const [registrandoAcuse, setRegistrandoAcuse] = useState<ControlDeEnvio | null>(null)
  const [generandoBalanza, setGenerandoBalanza] = useState(false)
  const [generandoSolicitud, setGenerandoSolicitud] = useState<'PL' | 'XF' | 'XC' | null>(null)
  const [resumen, setResumen] = useState<ResumenDeCliente | null>(null)
  const [grupo, setGrupo] = useState<string>()
  // Un solo «hoy» por montaje: si se recalculara en cada render, dos chips de la misma franja
  // podrían caer en días distintos al cruzar la medianoche.
  const hoy = useMemo(() => new Date(), [])

  async function recargar() {
    if (!clienteId) return
    // A2 · El calendario de plazos (RF-806) lo calcula el servidor con los días inhábiles
    // oficiales, y hoy el único sitio donde lo expone es el resumen del tablero: de ahí se toma
    // la fila de este cliente. Va por su cuenta y con su propio catch porque es información de
    // apoyo —si no llega, el control de envíos sigue sirviendo sin la franja—.
    void api.tablero()
      .then((filas) => setResumen(filas.find((fila) => fila.clienteId === clienteId) ?? null))
      .catch(() => setResumen(null))
    try {
      const [datosCliente, lista] = await Promise.all([api.obtenerCliente(clienteId), api.listarControlDeEnvios(clienteId)])
      setCliente(datosCliente)
      setControles(lista)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el control de envíos.')
    }
  }

  useEffect(() => {
    void recargar()
  }, [clienteId])

  async function marcarEnviado(control: ControlDeEnvio) {
    if (!clienteId) return
    setError('')
    try {
      await api.marcarEnviado(clienteId, control.id)
      avisar(`${NOMBRE_DE_TIPO_ARCHIVO[control.tipoArchivo]} del periodo ${periodo(control)} marcado como enviado.`)
      void recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo marcar como enviado.')
    }
  }

  // Si la primera carga falla, `cliente` y `controles` se quedan en undefined y la guarda de
  // abajo devolvía «Cargando…» para siempre con el error guardado pero invisible. El error
  // fatal se pinta antes; el de la línea siguiente sigue cubriendo los fallos con datos ya
  // en pantalla. Mismo patrón que BalanzaAjustada.
  if (error && (!cliente || !controles)) return <p className="error" role="alert">{error}</p>
  if (!cliente || !controles) return <p>Cargando…</p>

  const rechazados = controles.filter((control) => control.estado === 'Rechazado')
  const grupoActivo = GRUPOS_DE_ARCHIVO.find((g) => g.clave === grupo)
  const visibles = grupoActivo
    ? controles.filter((control) => grupoActivo.tipos.includes(control.tipoArchivo))
    : controles

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link></p>
      <header className="encabezado" data-recorrido="envios">
        <h1>Control de envíos</h1>
        {/* Los cuatro archivos se generaban desde el Catálogo, que no es donde se siguen: esta
            pantalla ya lista lo que producen —periodo, estado, acuse— y hasta ahora no tenía
            forma de generarlos. El CT se queda en el Catálogo porque el catálogo es su
            contenido y su bloqueo (RN-05) se explica allá. */}
        <span className="encabezado-acciones">
          <button className="secundario" onClick={() => setGenerandoBalanza(true)}>Generar balanza</button>
          <button className="ghost" onClick={() => setGenerandoSolicitud('PL')} title="Pólizas del periodo para una solicitud del SAT">
            Generar PL
          </button>
          <button className="ghost" onClick={() => setGenerandoSolicitud('XF')} title="Auxiliar de folios para una solicitud del SAT">
            Generar XF
          </button>
          <button className="ghost" onClick={() => setGenerandoSolicitud('XC')} title="Auxiliar de cuentas para una solicitud del SAT">
            Generar XC
          </button>
        </span>
      </header>

      {resumen && <FranjaDePlazos plazos={plazosDelCliente(resumen)} hoy={hoy} />}

      <p className="nota">
        El envío por el Buzón Tributario es manual: el contador genera el archivo aquí, lo sube
        al SAT y luego registra el acuse (folio y sello) o el motivo del rechazo (RF-805).
      </p>

      {error && <p className="error" role="alert">{error}</p>}

      {/* A3 · Los banners no se filtran con el segmentado: un rechazo del SAT es un bloqueo del
          cliente, no un renglón de la vista, y esconderlo al cambiar de segmento escondería
          justo lo que hay que atender. */}
      {rechazados.map((control) => (
        <BannerDeRechazo
          key={control.id}
          clienteId={clienteId!}
          control={control}
          sujeto={NOMBRE_DE_TIPO_ARCHIVO[control.tipoArchivo]}
        />
      ))}

      {controles.length === 0 ? (
        <p className="sutil">Todavía no se ha generado ningún archivo del Anexo 24 para este cliente.</p>
      ) : (
        <>
        <div className="encabezado" style={{ gap: 12, margin: '12px 0' }}>
          <div className="seg" role="radiogroup" aria-label="Tipo de archivo">
            <button type="button" className="seg-opcion" role="radio"
              aria-checked={grupoActivo === undefined} onClick={() => setGrupo(undefined)}>
              Todos · {controles.length}
            </button>
            {GRUPOS_DE_ARCHIVO.map((g) => (
              <button key={g.clave} type="button" className="seg-opcion" role="radio"
                aria-checked={grupoActivo?.clave === g.clave} onClick={() => setGrupo(g.clave)}>
                {g.etiqueta} · {controles.filter((control) => g.tipos.includes(control.tipoArchivo)).length}
              </button>
            ))}
          </div>
        </div>

        {visibles.length === 0 ? (
          <p className="vacio">Ningún archivo de tipo «{grupoActivo!.etiqueta}» para este cliente.</p>
        ) : (
        <div className="tabla-envoltura">
        <table className="tabla">
          <thead>
            <tr>
              <th>Periodo</th>
              <th>Archivo</th>
              <th style={{ whiteSpace: 'nowrap' }}>Archivo (RN-07)</th>
              <th>XSD</th>
              <th>Estado</th>
              <th>Generado</th>
              <th>Enviado</th>
              <th>Acuse</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((control) => (
              <tr key={control.id} className={control.estado === 'Rechazado' ? 'resaltada' : undefined}>
                <td className="mono">{periodo(control)}</td>
                <td>{NOMBRE_DE_TIPO_ARCHIVO[control.tipoArchivo]}</td>
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                  {control.nombreDeArchivo ?? <span className="sutil">—</span>}
                </td>
                <td><SelloXsd control={control} /></td>
                <td><span className={`insignia ${INSIGNIA_DE_ESTADO[control.estado]}`}>{control.estado}</span></td>
                <td className="sutil">{fecha(control.generadoEnUtc)}</td>
                <td className="sutil">{fecha(control.enviadoEnUtc)}</td>
                <td className="sutil">
                  {control.estado === 'Aceptado' && `Folio ${control.folioAcuse}`}
                  {control.estado === 'Rechazado' && `Motivo: ${control.motivoRechazo}`}
                  {/* RF-1604 · El folio es lo que el contador transcribió; esto es el acuse. */}
                  {control.nombreAcuseArchivo && (
                    <>
                      {' · '}
                      <button className="enlace" onClick={() => void descargarAcuse(control)}>
                        Acuse ({control.nombreAcuseArchivo})
                      </button>
                    </>
                  )}
                  {(control.estado === 'Generado' || control.estado === 'Enviado') && '—'}
                </td>
                <td>
                  {/* A5 · Rechazado ya no se queda sin acción: el catálogo es la raíz de los
                      rechazos del Anexo 24 (RN-05) y es lo que hay que corregir antes de
                      volver a generar.

                      RF-1600 + A5 · «Descargar» entrega el paquete RESGUARDADO, que es el que
                      se presentó byte a byte. Solo los controles anteriores al resguardo caen en
                      la regeneración, y entonces el aviso lo dice. Va en todos los estados
                      —incluido Aceptado— porque recuperar lo que se presentó es justo lo que
                      hace falta ante una revisión, y no altera el ciclo (RN-58). */}
                  <span className="acciones-de-fila">
                    <button className="enlace" disabled={descargando === control.id}
                      onClick={() => void descargar(control)}>
                      {descargando === control.id ? 'Generando…' : 'Descargar'}
                    </button>
                    <button className="enlace" onClick={() => void verGuia(control)}>Cómo se presenta</button>
                    {control.estado === 'Generado' && (
                      <button className="enlace" onClick={() => void marcarEnviado(control)}>Marcar enviado</button>
                    )}
                    {control.estado === 'Enviado' && (
                      <button className="enlace" onClick={() => setRegistrandoAcuse(control)}>Registrar acuse</button>
                    )}
                    {control.estado === 'Rechazado' && (
                      <Link className="enlace" to={`/clientes/${clienteId}/catalogo`}>Corregir catálogo</Link>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        )}
        </>
      )}

      {guia && <ModalDeGuia datos={guia} alCerrar={() => setGuia(null)} />}

      {registrandoAcuse && (
        <FormularioDeAcuse
          clienteId={clienteId!}
          control={registrandoAcuse}
          alCerrar={(mensaje) => {
            setRegistrandoAcuse(null)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {/* A diferencia del Catálogo, aquí sí se recarga al cerrar: el archivo recién generado
          es un renglón nuevo de esta tabla, y no verlo aparecer haría dudar de si se generó. */}
      {generandoBalanza && (
        <FormularioDeBalanza
          clienteId={clienteId!}
          alCerrar={(mensaje) => {
            setGenerandoBalanza(false)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {generandoSolicitud && (
        <FormularioDeSolicitudFiscal
          tipo={generandoSolicitud}
          clienteId={clienteId!}
          alCerrar={(mensaje) => {
            setGenerandoSolicitud(null)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}
    </>
  )
}

/** Resguarda el acuse del SAT: folio y sello si se aceptó, motivo si se rechazó (RF-805). */
function FormularioDeAcuse({
  clienteId,
  control,
  alCerrar,
}: {
  clienteId: string
  control: ControlDeEnvio
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [aceptado, setAceptado] = useState(true)
  const [folio, setFolio] = useState('')
  const [sello, setSello] = useState('')
  const [motivoRechazo, setMotivoRechazo] = useState('')
  // RF-1604 · el acuse que devolvió el SAT, tal cual, además del folio y el sello.
  const [archivo, setArchivo] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function guardar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError('')
    setEnviando(true)
    try {
      await api.registrarAcuse(clienteId, control.id, {
        aceptado,
        folio: aceptado ? folio : undefined,
        sello: aceptado ? sello : undefined,
        motivoRechazo: aceptado ? undefined : motivoRechazo,
      })

      // RF-1604 · El archivo va después del acuse y no antes: el dominio no resguarda el acuse
      // de algo que todavía no se envió, y así un fallo al subirlo no pierde folio y sello.
      if (archivo) await api.resguardarAcuseArchivo(clienteId, control.id, archivo)

      alCerrar(
        (aceptado ? 'Acuse de aceptación registrado.' : 'Rechazo registrado.') +
        (archivo ? ` Archivo del acuse resguardado: ${archivo.name}.` : ''),
      )
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo registrar el acuse.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={guardar}
      >
        <h2 id={idTitulo}>Registrar acuse del SAT</h2>
        <p className="sutil">
          {NOMBRE_DE_TIPO_ARCHIVO[control.tipoArchivo]} · periodo {periodo(control)}
        </p>

        <label>
          Resultado
          <select value={aceptado ? 'si' : 'no'} onChange={(e) => setAceptado(e.target.value === 'si')}>
            <option value="si">Aceptado</option>
            <option value="no">Rechazado</option>
          </select>
        </label>

        {aceptado ? (
          <>
            <label>
              Folio del acuse
              <input value={folio} onChange={(e) => setFolio(e.target.value)} required />
            </label>
            <label>
              Sello del acuse
              <input value={sello} onChange={(e) => setSello(e.target.value)} required />
            </label>
          </>
        ) : (
          <label>
            Motivo del rechazo
            <input value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)} required />
          </label>
        )}

        {/* RF-1604 · El folio y el sello son lo que tecleas; esto es lo que el SAT te devolvió.
            Opcional aquí a propósito: exigirlo dejaría fuera los acuses ya registrados sin él. */}
        <label>
          Archivo del acuse (PDF o ZIP, opcional)
          <input
            type="file"
            accept=".pdf,.zip,application/pdf,application/zip"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
        </label>
        <p className="menuda">
          Se conserva íntegro al menos cinco años (RN-09) y queda descargable desde esta pantalla.
        </p>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar acuse'}
          </button>
        </div>
      </form>
    </div>
  )
}

const MESES_DE_BALANZA = [
  '01 · Enero', '02 · Febrero', '03 · Marzo', '04 · Abril', '05 · Mayo', '06 · Junio',
  '07 · Julio', '08 · Agosto', '09 · Septiembre', '10 · Octubre', '11 · Noviembre', '12 · Diciembre',
  '13 · Balanza ajustada del cierre del ejercicio',
]

/** RF-802: genera el XML de balanza (BN/BC) validado contra el XSD oficial (G-02, G-03, G-04). */
function FormularioDeBalanza({
  clienteId,
  alCerrar,
}: {
  clienteId: string
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const hoy = new Date()
  const [anio, setAnio] = useState(hoy.getFullYear())
  const [mesPeriodo, setMesPeriodo] = useState(hoy.getMonth() + 1)
  const [tipoEnvio, setTipoEnvio] = useState<TipoEnvioBalanza>('Normal')
  const [fechaModBal, setFechaModBal] = useState('')
  const [error, setError] = useState('')
  const [generando, setGenerando] = useState(false)

  async function generar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError('')
    setGenerando(true)
    try {
      await api.descargarBalanza(clienteId, anio, mesPeriodo, tipoEnvio, tipoEnvio === 'Complementaria' ? fechaModBal : undefined)
      alCerrar('XML de balanza generado, validado contra el XSD oficial y descargado.')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo generar la balanza.')
    } finally {
      setGenerando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={generar}
      >
        <h2 id={idTitulo}>Generar balanza electrónica</h2>
        <p className="sutil">
          Balanza normal (BN) o complementaria (BC), con las mismas cifras que el reporte de
          balanza en pantalla. El periodo 13 reporta la balanza ajustada del cierre del ejercicio.
        </p>

        <label>
          Año
          <input type="number" min={2015} max={2099} value={anio} onChange={(e) => setAnio(Number(e.target.value))} required />
        </label>

        <label>
          Periodo
          <select value={mesPeriodo} onChange={(e) => setMesPeriodo(Number(e.target.value))}>
            {MESES_DE_BALANZA.map((nombre, i) => (
              <option key={i} value={i + 1}>{nombre}</option>
            ))}
          </select>
        </label>

        <label>
          Tipo de envío
          <select value={tipoEnvio} onChange={(e) => setTipoEnvio(e.target.value as TipoEnvioBalanza)}>
            <option value="Normal">Normal</option>
            <option value="Complementaria">Complementaria (corrige un periodo ya enviado)</option>
          </select>
        </label>

        {tipoEnvio === 'Complementaria' && (
          <div className="con-ayuda">
            <label>
              Fecha de la última modificación contable
              <input type="date" value={fechaModBal} onChange={(e) => setFechaModBal(e.target.value)} required />
            </label>
            <AyudaDeCampo clave="fecha-de-modificacion-de-balanza" />
          </div>
        )}

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={generando}>
            {generando ? 'Generando…' : 'Generar y descargar'}
          </button>
        </div>
      </form>
    </div>
  )
}

const MESES_DEL_ANIO = [
  '01 · Enero', '02 · Febrero', '03 · Marzo', '04 · Abril', '05 · Mayo', '06 · Junio',
  '07 · Julio', '08 · Agosto', '09 · Septiembre', '10 · Octubre', '11 · Noviembre', '12 · Diciembre',
]

const NOMBRE_DE_SOLICITUD: Record<'PL' | 'XF' | 'XC', string> = {
  PL: 'pólizas del periodo',
  XF: 'auxiliar de folios',
  XC: 'auxiliar de cuentas',
}

const NOMBRES_DE_TIPO_SOLICITUD: Record<TipoSolicitudFiscalizacion, string> = {
  ActoDeFiscalizacion: 'AF · Acto de fiscalización',
  FiscalizacionCompulsa: 'FC · Fiscalización compulsa',
  Devolucion: 'DE · Devolución',
  Compensacion: 'CO · Compensación',
}

/** RF-803: genera PL, XF o XC para una solicitud del SAT. AF/FC exigen NumOrden; DE/CO exigen NumTramite (G-05). */
function FormularioDeSolicitudFiscal({
  tipo,
  clienteId,
  alCerrar,
}: {
  tipo: 'PL' | 'XF' | 'XC'
  clienteId: string
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const hoy = new Date()
  const [anio, setAnio] = useState(hoy.getFullYear())
  const [mes, setMes] = useState(hoy.getMonth() + 1)
  const [tipoSolicitud, setTipoSolicitud] = useState<TipoSolicitudFiscalizacion>('ActoDeFiscalizacion')
  const [numOrden, setNumOrden] = useState('')
  const [numTramite, setNumTramite] = useState('')
  const [error, setError] = useState('')
  const [generando, setGenerando] = useState(false)

  const exigeOrden = tipoSolicitud === 'ActoDeFiscalizacion' || tipoSolicitud === 'FiscalizacionCompulsa'
  const exigeTramite = tipoSolicitud === 'Devolucion' || tipoSolicitud === 'Compensacion'

  async function generar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError('')
    setGenerando(true)
    try {
      const descargar =
        tipo === 'PL' ? api.descargarPolizasPeriodo : tipo === 'XF' ? api.descargarAuxiliarFolios : api.descargarAuxiliarCuentas
      await descargar(clienteId, anio, mes, tipoSolicitud, exigeOrden ? numOrden : undefined, exigeTramite ? numTramite : undefined)
      alCerrar(`Archivo de ${NOMBRE_DE_SOLICITUD[tipo]} generado, validado contra el XSD oficial y descargado.`)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : `No se pudo generar el archivo de ${NOMBRE_DE_SOLICITUD[tipo]}.`)
    } finally {
      setGenerando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={generar}
      >
        <h2 id={idTitulo}>Generar {NOMBRE_DE_SOLICITUD[tipo]} ({tipo})</h2>
        <p className="sutil">Para atender una solicitud del SAT: acto de fiscalización, compulsa, devolución o compensación (RF-803).</p>

        <label>
          Año
          <input type="number" min={2015} max={2099} value={anio} onChange={(e) => setAnio(Number(e.target.value))} required />
        </label>

        <label>
          Mes
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES_DEL_ANIO.map((nombre, i) => (
              <option key={i} value={i + 1}>{nombre}</option>
            ))}
          </select>
        </label>

        <div className="con-ayuda">
          <label>
            Tipo de solicitud
            <select value={tipoSolicitud} onChange={(e) => setTipoSolicitud(e.target.value as TipoSolicitudFiscalizacion)}>
              {Object.entries(NOMBRES_DE_TIPO_SOLICITUD).map(([valor, nombre]) => (
                <option key={valor} value={valor}>{nombre}</option>
              ))}
            </select>
          </label>
          <AyudaDeCampo clave="tipo-de-solicitud" />
        </div>

        {exigeOrden && (
          <label>
            Número de orden
            <input value={numOrden} onChange={(e) => setNumOrden(e.target.value)} placeholder="ABC123456/78" required />
          </label>
        )}

        {exigeTramite && (
          <label>
            Número de trámite
            <input value={numTramite} onChange={(e) => setNumTramite(e.target.value)} placeholder="1234567890" required />
          </label>
        )}

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={generando}>
            {generando ? 'Generando…' : 'Generar y descargar'}
          </button>
        </div>
      </form>
    </div>
  )
}
